import { FC } from "react"
import {
  PuxContent,
  PuxFooter,
  PuxIcon,
  PuxTypography,
  useTranslation,
} from "@phonero/pux-react"
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  Button,
  Input,
  ResponsiveMode,
  useResponsiveMode,
  WizardNavigationProps,
} from "@phonero/phonero-ui-lib"
import { ApiProductState, SimCardType2 } from "@phonero/common-graphql/types"
import css from "../SimCardWizard/SimCardWizard.module.scss"
import { InfoBox, StepButtons } from "@phonero/common-ux"
import { datacard, twinSim, warning } from "@phonero/pux-icons/icons"

interface ISelectSimCardTypeStepProps {
  simCardType: SimCardType2 | undefined
  setSimCardType: (input: SimCardType2 | undefined) => void
  availableCards: any
}
export const SelectSimCardTypeStep: FC<
  ISelectSimCardTypeStepProps & WizardNavigationProps
> = ({ simCardType, availableCards, setSimCardType, onNext }) => {
  const { t, i18n } = useTranslation()
  const responsiveMode = useResponsiveMode()
  const onChangeSimCardType = (_index: number) => {
    const index = Number(_index)
    if (isNaN(index)) {
      console.error("received NaN for index", _index)
      return null
    }
    const cardType = index === 0 ? SimCardType2.Datacard : SimCardType2.Twin
    setSimCardType(cardType)
  }
  const availableDatacard = availableCards.find(
    (c) => c.type === SimCardType2.Datacard
  )
  const availableTwinCard = availableCards.find(
    (c) => c.type === SimCardType2.Twin
  )
  const hasActiveExcludedByProduct = availableCards.filter(
    (c) =>
      c.excludedBy &&
      c.excludedBy.some(
        (exclusion) =>
          exclusion.productId === 11061 &&
          exclusion.state === ApiProductState.Active
      )
  )

  const nextEnabled = () => {
    if (!simCardType) {
      return false
    }
    return true
  }

  return (
    <>
      <PuxContent>
        <PuxTypography variant="h3" className={css["subHeader"]}>
          {t("Subscription.AddNewSubscriptionSimCard.SelectSimCard")}
        </PuxTypography>

        <Accordion
          className={
            responsiveMode === ResponsiveMode.Desktop
              ? css["radio-container-vertical"]
              : css["radio-container-horizontal"]
          }
          style={{ paddingTop: 0 }}
          index={Number(simCardType)}
          onChange={(index: number) => onChangeSimCardType(index)}
          onPointerEnterCapture={() => {}}
          onPointerLeaveCapture={() => {}}
        >
          {responsiveMode === ResponsiveMode.Mobile && !availableDatacard && (
            <div className={css.simCardWarningMobile}>
              <p>{t("Subscription.MaxDataSimMessage")}</p>
            </div>
          )}
          <div style={{ position: "relative" }}>
            <AccordionItem
              style={{ width: "95%", margin: "auto" }}
              className={css["radio-container-vertical-left"]}
            >
              {responsiveMode === ResponsiveMode.Desktop &&
                !availableDatacard && (
                  <div className={css.simCardWarning}>
                    <p>{t("Subscription.MaxDataSimMessage")}</p>
                  </div>
                )}
              <div style={{ opacity: availableDatacard ? 1 : 0.4 }}>
                <AccordionButton
                  as={Button}
                  left={
                    <Input
                      type="radio"
                      name="simCardType"
                      checked={simCardType === SimCardType2.Datacard}
                      readOnly
                      tabIndex={-1}
                    />
                  }
                >
                  <div slot="start">
                    <PuxIcon
                      style={{ color: "var(--pux-color-primary)" }}
                      size="large"
                      icon={datacard}
                    />
                  </div>
                  <div style={{ textAlign: "left", padding: "2rem 1rem" }}>
                    <div>
                      <div className={css["simCardType"]}>
                        {t("Subscription.DataSimCard")}
                      </div>
                      <div
                        className={css["radio-container-vertical-height"]}
                        style={{
                          alignItems: "center",
                          display: "flex",
                          color: "#040D2999",
                          fontWeight: "bold",
                        }}
                      >
                        <PuxTypography variant="label" component={"span"}>
                          {t(
                            "Subscription.AddNewSubscriptionSimCard.DataSimDescription"
                          )}
                        </PuxTypography>
                      </div>
                    </div>
                  </div>
                  <div style={{ float: "right", marginLeft: "auto" }}>
                    <InfoBox
                      size={20}
                      noSpaceAround
                      activateStopPropagation
                      message={t(
                        "Subscription.AddNewSubscriptionSimCard.DataSimInfo"
                      )}
                    />
                  </div>
                </AccordionButton>
              </div>
            </AccordionItem>
          </div>
          {responsiveMode === ResponsiveMode.Mobile && (
            <>
              {!availableTwinCard && (
                <div className={css.simCardWarningMobile}>
                  <p>{t("Subscription.MaxTwinSimMessage")}</p>
                </div>
              )}
              {hasActiveExcludedByProduct.length > 0 && (
                <div className={css.simCardWarningMobile}>
                  <p>
                    {t("Subscription.ExcludedByProductMessage", {
                      context: "SMSRecording",
                    })}
                  </p>
                </div>
              )}
            </>
          )}
          <div style={{ position: "relative" }}>
            <AccordionItem
              style={{ width: "95%", margin: "auto" }}
              className={css["radio-container-vertical-right"]}
            >
              {responsiveMode === ResponsiveMode.Desktop && (
                <>
                  {!availableTwinCard && (
                    <div className={css.simCardWarning}>
                      <p>{t("Subscription.MaxTwinSimMessage")}</p>
                    </div>
                  )}
                  {hasActiveExcludedByProduct.length > 0 && (
                    <div className={css.simCardWarning}>
                      <p>
                        {t("Subscription.ExcludedByProductMessage", {
                          context: "SMSRecording",
                        })}
                      </p>
                    </div>
                  )}
                </>
              )}
              <div
                style={{
                  opacity:
                    availableTwinCard && hasActiveExcludedByProduct.length === 0
                      ? 1
                      : 0.4,
                }}
              >
                <AccordionButton
                  as={Button}
                  left={
                    <Input
                      type="radio"
                      name="simCardType"
                      checked={simCardType === SimCardType2.Twin}
                      readOnly
                      tabIndex={-1}
                    />
                  }
                >
                  <div slot="start">
                    <PuxIcon
                      style={{ color: "var(--pux-color-primary)" }}
                      icon={twinSim}
                      size="large"
                    />
                  </div>
                  <div style={{ textAlign: "left", padding: "2rem 1rem" }}>
                    <div>
                      <div className={css["simCardType"]}>
                        {t("Subscription.TwinSimCard")}
                      </div>
                      <div
                        className={css["radio-container-vertical-height"]}
                        style={{
                          alignItems: "center",
                          display: "flex",
                          color: "#040D2999",
                          fontWeight: 600,
                        }}
                      >
                        <PuxTypography variant="label" component={"span"}>
                          {t(
                            "Subscription.AddNewSubscriptionSimCard.TwinSimDescription"
                          )}
                        </PuxTypography>
                      </div>
                    </div>
                  </div>
                  <div style={{ float: "right", marginLeft: "auto" }}>
                    <InfoBox
                      size={20}
                      noSpaceAround
                      activateStopPropagation
                      message={t(
                        "Subscription.AddNewSubscriptionSimCard.TwinSimInfo"
                      )}
                    />
                  </div>
                </AccordionButton>
              </div>
            </AccordionItem>
          </div>
        </Accordion>
      </PuxContent>
      <PuxFooter>
        <StepButtons onNext={onNext} nextDisabled={!nextEnabled()} />
      </PuxFooter>
    </>
  )
}
